<template>
  <div class="page-container material-page">
    <div class="stats-row">
      <div class="stat-item">
        <div class="stat-item-value">{{ stats.totalCount || 0 }}</div>
        <div class="stat-item-label">材料种类</div>
      </div>
      <div class="stat-item success">
        <div class="stat-item-value">{{ stats.sufficientCount || 0 }}</div>
        <div class="stat-item-label">库存充足</div>
      </div>
      <div class="stat-item warning">
        <div class="stat-item-value">{{ stats.warningCount || 0 }}</div>
        <div class="stat-item-label">库存预警</div>
      </div>
      <div class="stat-item danger">
        <div class="stat-item-value">{{ stats.lowCount || 0 }}</div>
        <div class="stat-item-label">库存不足</div>
      </div>
      <div class="stat-item warning">
        <div class="stat-item-value">{{ stats.pendingCount || 0 }}</div>
        <div class="stat-item-label">待审批</div>
      </div>
    </div>

    <div class="card">
      <div class="tabs-header-wrapper">
        <el-tabs v-model="activeTab" class="main-tabs" @tab-click="handleTabClick">
          <el-tab-pane label="材料台账" name="ledger"/>
          <el-tab-pane label="入库记录" name="stockIn"/>
          <el-tab-pane name="applyOut">
            <span slot="label" class="tab-label-with-badge">
              领用记录
              <el-badge
                v-if="(stats.pendingCount || 0) > 0"
                :max="99"
                :value="stats.pendingCount || 0"
                class="apply-out-tab-badge"
              />
            </span>
          </el-tab-pane>
        </el-tabs>

        <div v-if="activeTab === 'ledger'" class="action-btn-group">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增材料</el-button>
          <el-button type="success" size="small" icon="el-icon-upload2" @click="handleImport">Excel导入</el-button>
        </div>
      </div>

      <!-- ===== Tab1: 材料台账 ===== -->
      <template v-if="activeTab === 'ledger'">
        <div class="search-bar">
          <el-input
            v-model="ledgerQuery.keyword"
            placeholder="材料名称/备注"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleQuery"
          />
          <dict-select
            v-model="ledgerQuery.materialType"
            :dict-type="DICT_TYPE.MATERIAL_TYPE"
            placeholder="材料类型"
            clearable
            style="width: 150px"
            @change="handleQuery"
          />
          <el-select
            v-model="ledgerQuery.stockStatus"
            placeholder="库存状态"
            clearable
            style="width: 120px"
            @change="handleQuery"
          >
            <el-option label="库存充足" value="sufficient" />
            <el-option label="库存预警" value="warning" />
            <el-option label="库存不足" value="low" />
          </el-select>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleQuery">搜索</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </div>

        <div class="table-wrapper">
          <el-table
            key="ledger-table"
            v-loading="loading"
            row-key="uuid"
            :data="ledgerData"
            border
            stripe
            style="width: 100%"
            height="100%"
            :header-cell-style="{ textAlign: 'center' }"
            :cell-style="{ textAlign: 'center' }"
            :row-class-name="ledgerRowClass"
          >
            <el-table-column label="材料编号" prop="materialCode" width="90" />
            <el-table-column label="材料名称" prop="materialName" min-width="140" show-overflow-tooltip />
            <el-table-column label="材料类型" prop="materialType" width="120">
              <template slot-scope="{ row }">
                <dict-tag :dict-type="DICT_TYPE.MATERIAL_TYPE" :value="row.materialType" />
              </template>
            </el-table-column>
            <el-table-column label="当前库存" prop="stockCount" width="100">
              <template slot-scope="{ row }">
                <span :class="stockCountClass(row)">{{ row.stockCount }}</span>
              </template>
            </el-table-column>
            <el-table-column label="计量单位" prop="unit" width="90" />
            <el-table-column label="预警阈值" prop="warningThreshold" width="100" />
            <el-table-column label="库存状态" prop="stockStatus" width="100">
              <template slot-scope="{ row }">
                <el-tag :type="stockStatusType(row.stockStatus)" size="small">
                  {{ row.stockStatusName || row.stockStatus }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="存放位置" prop="storageLocation" min-width="120" show-overflow-tooltip />
            <el-table-column label="操作" width="320" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="primary" plain size="mini" @click="openMaterialDetail(row)">查看详情</el-button>
                <el-button type="success" plain size="mini" @click="handleStockIn(row)">入库</el-button>
                <el-button :disabled="row.stockCount <= 0" type="warning" plain size="mini" @click="handleApplyOut(row)">领用申请</el-button>
                <el-button type="info" plain size="mini" @click="handleEdit(row)">编辑</el-button>
                <el-button type="danger" plain size="mini" @click="handleDelete(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <el-pagination
          class="pagination"
          :current-page="ledgerPage.pageNo"
          :page-sizes="[10, 20, 50]"
          :page-size="ledgerPage.pageSize"
          :total="ledgerPage.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLedgerSizeChange"
          @current-change="handleLedgerPageChange"
        />
      </template>

      <!-- ===== Tab2: 入库记录 ===== -->
      <template v-if="activeTab === 'stockIn'">
        <div class="search-bar">
          <el-input
            v-model="stockInQuery.keyword"
            placeholder="材料名称/来源/备注"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleStockInSearch"
          />
          <el-date-picker
            v-model="stockInDateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="入库时间起"
            end-placeholder="入库时间止"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            style="width: 280px"
            @change="handleStockInDateChange"
          />
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleStockInSearch">搜索</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="handleStockInReset">重置</el-button>
        </div>
        <div class="table-wrapper">
          <el-table
            key="stockIn-table"
            v-loading="loading"
            :row-key="stockInRowKey"
            :data="stockInData"
            border
            stripe
            style="width: 100%"
            height="100%"
            :header-cell-style="{ textAlign: 'center' }"
            :cell-style="{ textAlign: 'center' }"
          >
            <el-table-column label="材料编号" prop="materialCode" width="90" />
            <el-table-column label="材料名称" prop="materialName" min-width="140" show-overflow-tooltip />
            <el-table-column label="入库数量" prop="inCount" width="100">
              <template slot-scope="{ row }">{{ row.inCount }}{{ row.unit }}</template>
            </el-table-column>
            <el-table-column label="入库日期" prop="inDate" width="160">
              <template slot-scope="{ row }">{{ formatDate(row.inDate) }}</template>
            </el-table-column>
            <el-table-column label="材料来源" prop="supplier" min-width="140" show-overflow-tooltip />
            <el-table-column label="操作人" prop="operatorName" width="100" />
            <el-table-column label="备注" prop="remark" min-width="140" show-overflow-tooltip />
            <el-table-column label="记录时间" prop="createTime" width="160">
              <template slot-scope="{ row }">{{ formatDateTime(row.createTime) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="120" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="primary" plain size="mini" @click="openStockInDetail(row)">查看详情</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <el-pagination
          class="pagination"
          :current-page="stockInPage.pageNo"
          :page-sizes="[10, 20, 50]"
          :page-size="stockInPage.pageSize"
          :total="stockInPage.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleStockInSizeChange"
          @current-change="handleStockInPageChange"
        />
      </template>

      <!-- ===== Tab3: 领用记录 ===== -->
      <template v-if="activeTab === 'applyOut'">
        <div class="search-bar apply-out-toolbar">
          <span class="filter-label">快捷筛选</span>
          <el-radio-group
            v-model="outQuery.quick"
            class="apply-out-quick-group"
            size="small"
            @change="handleApplyOutFilterChange"
          >
            <el-radio-button label="all">全部</el-radio-button>
            <el-radio-button label="pending">待处理</el-radio-button>
            <el-radio-button label="approved">已通过</el-radio-button>
            <el-radio-button label="rejected">已拒绝</el-radio-button>
          </el-radio-group>
          <div class="apply-out-extra-filters">
            <el-input
              v-model="outQuery.keyword"
              placeholder="材料名称/用途说明"
              clearable
              style="width: 200px"
              @keyup.enter.native="handleApplyOutDetailSearch"
            />
            <el-input
              v-model="outQuery.applyUserKeyword"
              placeholder="申请人"
              clearable
              style="width: 140px"
              @keyup.enter.native="handleApplyOutDetailSearch"
            />
            <el-date-picker
              v-model="outApplyDateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="申请时间起"
              end-placeholder="申请时间止"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              style="width: 280px"
              @change="handleApplyOutDateChange"
            />
            <el-button type="primary" size="small" icon="el-icon-search" @click="handleApplyOutDetailSearch">搜索</el-button>
            <el-button size="small" icon="el-icon-refresh" @click="handleApplyOutDetailReset">重置</el-button>
          </div>
        </div>
        <div class="table-wrapper">
          <el-table
            key="applyOut-table"
            v-loading="loading"
            row-key="uuid"
            :data="applyOutData"
            border
            stripe
            style="width: 100%"
            height="100%"
            :header-cell-style="{ textAlign: 'center' }"
            :cell-style="{ textAlign: 'center' }"
          >
            <el-table-column label="领用单号" prop="materialOutCode" width="160" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <code v-if="row.materialOutCode">{{ row.materialOutCode }}</code>
                <span v-else class="no-action">-</span>
              </template>
            </el-table-column>
            <el-table-column label="材料名称" prop="materialName" min-width="140" show-overflow-tooltip />
            <el-table-column label="领用数量" prop="outCount" width="80">
              <template slot-scope="{ row }">{{ row.outCount }}{{ row.unit }}</template>
            </el-table-column>
            <el-table-column label="用途说明" prop="purpose" min-width="150" show-overflow-tooltip />
            <el-table-column label="关联故障单" width="140" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <el-link
                  v-if="row.faultCode"
                  type="primary"
                  :underline="false"
                  @click="goFaultList(row.faultCode)"
                >{{ row.faultCode }}</el-link>
                <span v-else class="no-action">-</span>
              </template>
            </el-table-column>
            <el-table-column label="使用区域" prop="zoneAreaName" width="120" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span>{{ row.zoneAreaName || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="状态" prop="status" width="80">
              <template slot-scope="{ row }">
                <el-tag :type="outStatusType(row.status)" size="small">{{ row.statusName || row.status }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="申请人" prop="applyUserName" width="100" />
            <el-table-column label="申请时间" prop="applyTime" width="150">
              <template slot-scope="{ row }">{{ formatDateTime(row.applyTime) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="270" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="primary" plain size="mini" @click="openOutDetail(row)">查看详情</el-button>
                <el-button
                  v-if="row.outFileId && (row.status === 'pending' || row.status === 'approved')"
                  type="warning"
                  plain
                  size="mini"
                  @click="handleDownloadApplyFile(row)"
                >下载领用单</el-button>
                <template v-if="row.status === 'pending'">
                  <el-button type="success" plain size="mini" @click="handleApprove(row)">通过</el-button>
                  <el-button type="danger" plain size="mini" @click="handleReject(row)">拒绝</el-button>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <el-pagination
          class="pagination"
          :current-page="outPage.pageNo"
          :page-sizes="[10, 20, 50]"
          :page-size="outPage.pageSize"
          :total="outPage.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleOutSizeChange"
          @current-change="handleOutPageChange"
        />
      </template>
    </div>

    <!-- 新增编辑材料弹窗 -->
    <MaterialDialog ref="materialDialog" @success="handleDialogSuccess" />
    <!-- 入库弹窗 -->
    <StockInDialog ref="stockInDialog" @success="handleDialogSuccess" />
    <!-- 领用弹窗 -->
    <ApplyOutDialog ref="applyOutDialog" @success="handleDialogSuccess" />
    <!-- 材料详情（台账 / 入库） -->
    <MaterialDetailDialog
      v-if="materialDetailVisible"
      :visible.sync="materialDetailVisible"
      :detail="materialDetailData"
      :mode="materialDetailMode"
    />
    <!-- 领用记录详情（独立接口） -->
    <MaterialOutDetailDialog
      v-if="outDetailVisible"
      :visible.sync="outDetailVisible"
      :uuid="outDetailUuid"
    />
    <ImportMaterialDialog :visible.sync="importVisible" @success="handleImportSuccess" />
  </div>
</template>

<script>
// 服务
import { materialService } from './material.service';
// 工具函数
import { DICT_TYPE } from '@/features/components/dict/dictTypeKeys';
import { formatDate, formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import { downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil';
// 组件
import MaterialDialog from './materialDialog.vue';
import StockInDialog from './stockInDialog.vue';
import ApplyOutDialog from './applyOutDialog.vue';
import MaterialDetailDialog from './materialDetailDialog.vue';
import MaterialOutDetailDialog from './materialOutDetailDialog.vue';
import ImportMaterialDialog from './importMaterialDialog.vue';

export default {
  name: 'MaterialPage',
  components: { MaterialDialog, StockInDialog, ApplyOutDialog, MaterialDetailDialog, MaterialOutDetailDialog, ImportMaterialDialog },
  data() {
    return {
      activeTab: 'ledger',
      loading: false,
      stats: {},

      DICT_TYPE,
      // Tab1 材料台账
      ledgerQuery: { keyword: '', materialType: '', stockStatus: '' },
      ledgerData: [],
      ledgerPage: { pageNo: 1, pageSize: 10, total: 0 },

      // Tab2 入库记录
      stockInData: [],
      stockInPage: { pageNo: 1, pageSize: 10, total: 0 },

      stockInQuery: {
        keyword: '',
        inDateStart: null,
        inDateEnd: null
      },
      stockInDateRange: null,

      // Tab3 领用：快捷 + 台账式细筛
      outQuery: {
        quick: 'all',
        keyword: '',
        applyUserKeyword: '',
        applyTimeStart: null,
        applyTimeEnd: null
      },
      outApplyDateRange: null,
      applyOutData: [],
      outPage: { pageNo: 1, pageSize: 10, total: 0 },

      materialDetailVisible: false,
      materialDetailData: null,
      materialDetailMode: 'material',

      outDetailVisible: false,
      outDetailUuid: '',

      importVisible: false
    };
  },
  created() {
    this.loadLedger();
    this.loadStats();
  },
  methods: {
    handleTabClick(tab) {
      if (tab.name === 'ledger') this.loadLedger();
      else if (tab.name === 'stockIn') this.loadStockIn();
      else if (tab.name === 'applyOut') {
        this.outQuery.quick = this.stats.pendingCount > 0 ? 'pending' : 'all';
        this.loadApplyOut();
      }
      this.loadStats();
    },

    // ===== 统计 =====
    loadStats() {
      materialService.getStatistics().then(res => {
        this.stats = res.data || {};
      });
    },

    // ===== Tab1 材料台账 =====
    loadLedger() {
      this.loading = true;
      materialService.queryMaterialPage({
        pageNo: this.ledgerPage.pageNo,
        pageSize: this.ledgerPage.pageSize,
        keyword: this.ledgerQuery.keyword || undefined,
        materialType: this.ledgerQuery.materialType || undefined,
        stockStatus: this.ledgerQuery.stockStatus || undefined
      }).then(res => {
        const page = res.data || {};
        this.ledgerData = page.rows || [];
        this.ledgerPage.total = page.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    handleQuery() {
      this.ledgerPage.pageNo = 1;
      this.loadLedger();
      this.loadStats();
    },
    handleReset() {
      this.ledgerQuery = { keyword: '', materialType: '', stockStatus: '' };
      this.ledgerPage.pageNo = 1;
      this.loadLedger();
    },
    handleImport() {
      this.importVisible = true;
    },
    handleImportSuccess() {
      this.$message.success('导入完成');
      this.loadLedger();
      this.loadStats();
    },
    handleLedgerSizeChange(size) {
      this.ledgerPage.pageSize = size;
      this.loadLedger();
    },
    handleLedgerPageChange(page) {
      this.ledgerPage.pageNo = page;
      this.loadLedger();
    },
    openMaterialDetail(row) {
      this.materialDetailMode = 'material';
      this.materialDetailData = row;
      this.materialDetailVisible = true;
    },
    openOutDetail(row) {
      this.outDetailUuid = row.uuid || '';
      this.outDetailVisible = true;
    },
    openStockInDetail(row) {
      this.materialDetailMode = 'stockIn';
      this.materialDetailData = row;
      this.materialDetailVisible = true;
    },
    handleAdd() {
      this.$refs.materialDialog.open(null);
    },
    handleEdit(row) {
      this.$refs.materialDialog.open(row);
    },
    handleStockIn(row) {
      this.$refs.stockInDialog.open(row);
    },
    handleApplyOut(row) {
      this.$refs.applyOutDialog.open(row);
    },
    handleDelete(row) {
      this.$confirm(`确定删除材料「${row.materialName}」？删除后不可恢复。`, '提示', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }).then(() => {
        this.loading = true;
        materialService.deleteMaterial(row.uuid).then(() => {
          this.$message.success('删除成功');
          this.loadLedger();
          this.loadStats();
        }).finally(() => {
          this.loading = false;
        });
      });
    },
    /** 对话框成功回调 */
    handleDialogSuccess() {
      if (this.activeTab === 'ledger') this.loadLedger();
      else if (this.activeTab === 'stockIn') this.loadStockIn();
      else if (this.activeTab === 'applyOut') {
        this.outQuery.quick = this.stats.pendingCount > 0 ? 'pending' : 'all';
        this.loadApplyOut();
      }
      this.loadStats();
    },
    /** 领用记录过滤器变化回调 */
    handleApplyOutFilterChange() {
      this.outPage.pageNo = 1;
      this.loadApplyOut();
    },
    handleApplyOutDetailSearch() {
      this.outPage.pageNo = 1;
      this.loadApplyOut();
    },
    handleApplyOutDetailReset() {
      this.outQuery.keyword = '';
      this.outQuery.applyUserKeyword = '';
      this.outQuery.applyTimeStart = null;
      this.outQuery.applyTimeEnd = null;
      this.outApplyDateRange = null;
      this.outPage.pageNo = 1;
      this.loadApplyOut();
    },
    handleApplyOutDateChange(val) {
      if (val && val.length === 2) {
        this.outQuery.applyTimeStart = val[0];
        this.outQuery.applyTimeEnd = val[1];
      } else {
        this.outQuery.applyTimeStart = null;
        this.outQuery.applyTimeEnd = null;
      }
      this.handleApplyOutDetailSearch();
    },

    // ===== Tab2 入库记录 =====
    loadStockIn() {
      this.loading = true;
      materialService.queryMaterialInPage({
        pageNo: this.stockInPage.pageNo,
        pageSize: this.stockInPage.pageSize,
        keyword: this.stockInQuery.keyword || undefined,
        inDateStart: this.stockInQuery.inDateStart || undefined,
        inDateEnd: this.stockInQuery.inDateEnd || undefined
      }).then(res => {
        const page = res.data || {};
        this.stockInData = page.rows || [];
        this.stockInPage.total = page.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    /** 入库记录大小变化回调 */
    handleStockInSizeChange(size) {
      this.stockInPage.pageSize = size;
      this.loadStockIn();
    },
    /** 入库记录页码变化回调 */
    handleStockInPageChange(page) {
      this.stockInPage.pageNo = page;
      this.loadStockIn();
    },
    handleStockInSearch() {
      this.stockInPage.pageNo = 1;
      this.loadStockIn();
    },
    handleStockInReset() {
      this.stockInQuery = {
        keyword: '',
        inDateStart: null,
        inDateEnd: null
      };
      this.stockInDateRange = null;
      this.stockInPage.pageNo = 1;
      this.loadStockIn();
    },
    handleStockInDateChange(val) {
      if (val && val.length === 2) {
        this.stockInQuery.inDateStart = val[0];
        this.stockInQuery.inDateEnd = val[1];
      } else {
        this.stockInQuery.inDateStart = null;
        this.stockInQuery.inDateEnd = null;
      }
      this.handleStockInSearch();
    },

    // ===== Tab3 领用记录 =====
    loadApplyOut() {
      this.loading = true;
      const params = {
        pageNo: this.outPage.pageNo,
        pageSize: this.outPage.pageSize,
        keyword: this.outQuery.keyword || undefined,
        applyUserKeyword: this.outQuery.applyUserKeyword || undefined,
        applyTimeStart: this.outQuery.applyTimeStart || undefined,
        applyTimeEnd: this.outQuery.applyTimeEnd || undefined
      };
      const q = this.outQuery.quick;
      if (q === 'pending') {
        params.statusList = ['pending'];
      } else if (q === 'approved') {
        params.statusList = ['approved'];
      } else if (q === 'rejected') {
        params.statusList = ['rejected'];
      }
      materialService.queryMaterialOutPage(params).then(res => {
        const page = res.data || {};
        this.applyOutData = page.rows || [];
        this.outPage.total = page.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    /** 通过领用申请 */
    handleApprove(row) {
      const out = row.outCount != null ? row.outCount : 0;
      this.$confirm(`确定通过「${row.materialName}」的领用申请（数量：${out}${row.unit}）？\n通过后库存立即扣减，不可撤销。`, '审批确认', {
        type: 'warning',
        confirmButtonText: '确定通过',
        cancelButtonText: '取消'
      }).then(() => {
        this.loading = true;
        materialService.approveOut({ uuid: row.uuid }).then(() => {
          this.$message.success('审批通过');
          this.loadApplyOut();
          this.loadStats();
        }).finally(() => {
          this.loading = false;
        });
      }).catch(() => {});
    },
    /** 拒绝领用申请 */
    handleReject(row) {
      this.$prompt('请填写拒绝原因', '审批拒绝', {
        confirmButtonText: '确认拒绝',
        cancelButtonText: '取消',
        inputPlaceholder: '请填写拒绝原因（必填）',
        inputValidator: (value) => {
          const v = (value || '').trim();
          if (!v) {
            return '请填写拒绝原因';
          }
          return true;
        },
        inputErrorMessage: '请填写拒绝原因'
      }).then(({ value }) => {
        this.loading = true;
        const remark = (value || '').trim();
        materialService.rejectOut({ uuid: row.uuid, remark }).then(() => {
        this.$message.success('已拒绝');
        this.loadApplyOut();
        this.loadStats();
        }).finally(() => {
          this.loading = false;
        });
      }).catch(() => {});
    },
    /** 领用记录大小变化回调 */
    handleOutSizeChange(size) {
      this.outPage.pageSize = size;
      this.loadApplyOut();
    },
    /** 领用记录页码变化回调 */
    handleOutPageChange(page) {
      this.outPage.pageNo = page;
      this.loadApplyOut();
    },

    /** 下载领用单 */
    handleDownloadApplyFile(row) {
      this.loading = true;
      materialService.downloadApplyFile(row.uuid).then((res) => {
        const fileLabel = row.materialOutCode || row.materialCode || row.uuid;
        downloadFromApiBlobResponse(res, `材料领用单-${fileLabel}.docx`);
      }).finally(() => {
        this.loading = false;
      });
    },
    /** 跳转故障台账并按故障编号筛选（全部 Tab，避免已结案单被待处理视图过滤掉） */
    goFaultList(faultCode) {
      const code = (faultCode || '').trim();
      if (!code) return;
      this.$router.push({ name: 'faultList', query: { keyword: code, listTab: 'all' } }).catch(() => {});
    },

    /** 入库记录行键 */
    stockInRowKey(row) {
      return row.uuid || row.id || `${row.materialCode || ''}-${row.createTime || ''}-${row.inDate || ''}`;
    },

    // ===== 工具方法 =====
    ledgerRowClass({ row }) {
      if (row.stockStatus === 'low') return 'row-danger';
      if (row.stockStatus === 'warning') return 'row-warning';
      return '';
    },
    stockCountClass(row) {
      if (row.stockStatus === 'low') return 'text-danger';
      if (row.stockStatus === 'warning') return 'text-warning';
      return '';
    },
    stockStatusType(status) {
      const map = { sufficient: 'success', warning: 'warning', low: 'danger' };
      return map[status] || 'info';
    },
    outStatusType(status) {
      const map = { pending: 'warning', approved: 'success', rejected: 'danger' };
      return map[status] || 'info';
    },
    formatDateTime,
    formatDate
  }
};
</script>

<style lang="scss" scoped>
.material-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .stats-row {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 15px;
    margin-bottom: 10px;

    .stat-item {
      background: #fff;
      border-radius: 8px;
      padding: 18px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .stat-item-value {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 5px;
        color: #303133;
      }

      .stat-item-label {
        font-size: 14px;
        color: #909399;
      }

      &.success .stat-item-value { color: #67c23a; }
      &.warning .stat-item-value { color: #e6a23c; }
      &.danger .stat-item-value { color: #f56c6c; }
      &.info .stat-item-value { color: #409eff; }
    }
  }

  .card {
    flex: 1;
    min-height: 0;
    background: #fff;
    border-radius: 8px;
    padding: 10px 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    .tabs-header-wrapper {
      position: relative;

      ::v-deep .el-tabs__header {
        margin-bottom: 10px;
      }

      ::v-deep .el-tabs__nav-wrap::after {
        height: 1px;
        background-color: #ebeef5;
      }

      ::v-deep .el-tabs__item {
        font-size: 15px;
        font-weight: 500;
        height: 45px;
        line-height: 45px;
      }

      .action-btn-group {
        position: absolute;
        right: 0;
        top: 8px;
        display: flex;
        gap: 10px;
        z-index: 1;
      }

      .tab-label-with-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }

      .apply-out-tab-badge {
        vertical-align: middle;
        margin-top: -4px;
        ::v-deep .el-badge__content {
          border: none;
        }
      }
    }

    .apply-out-toolbar {
      align-items: center;

      .filter-label {
        flex-shrink: 0;
        font-size: 13px;
        color: #606266;
      }

      .apply-out-quick-group {
        flex-shrink: 0;
        flex-wrap: wrap;
      }

      .apply-out-extra-filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
        margin-left: auto;
      }
    }

    .search-bar {
      flex-shrink: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      padding: 10px;
      background: #f5f7fa;
      border-radius: 4px;
    }

    .table-wrapper {
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
  }

  .pagination {
    flex-shrink: 0;
    margin-top: 20px;
    text-align: right;
  }
}

.text-danger { color: #f56c6c; font-weight: 600; }
.text-warning { color: #e6a23c; font-weight: 600; }
.no-action { color: #c0c4cc; }
</style>

<style>
.material-page .row-danger td { background-color: #fff0f0 !important; }
.material-page .row-warning td { background-color: #fffbe6 !important; }
</style>
